<template>
  <div v-if="showAllCategory" class="mask" @click.stop="showAllCategory = false"></div>
  <div class="search-bar-wrapper">
    <div class="left-options">
      <van-popover v-model:show="showSort" :actions="sortActions" @select="onSelect" overlay :overlay-style="{ background: 'transparent' }">
        <template #reference>
          <p class="reference-text">{{ currentSort?.text }}&nbsp;<van-icon :name="showSort ? 'arrow-up' : 'arrow-down'" /></p>
        </template>

        <template #action="{ action, index }">
          <p class="action-text" :data-selected="sortActions[index].act ? 1 : 0">{{ action.text }}</p>
        </template>
      </van-popover>
      <van-popover v-model:show="showFilter" placement="bottom-start" :offset="[-106, 20]" overlay :overlay-style="{ background: 'transparent' }">
        <template #reference>
          <p class="reference-text">Filter&nbsp;<van-icon name="filter-o" /></p>
        </template>

        <div class="filter-container">
          <div class="close-icon" @click="showFilter = false"><van-icon name="cross" /></div>
          <div class="inner-filter-container">
            <div class="filter-item">
              <p class="label">Price</p>
              <div class="values filter-price-values">
                <div class="filter-price">
                  <span class="price-input"><van-field v-model="params.priceFrom"  type="digit" label="" placeholder="Min" style="width: 100px;" /></span>
                  <span style="margin: 0 10px;">-</span>
                  <span class="price-input" style="margin-right: 10px;"><van-field  v-model="params.priceTo" type="digit" label="" placeholder="Max" style="width: 100px;" /></span>
                </div>
                <van-button type="primary" @click="handleApplyPriceFilter">Apply</van-button>
              </div>
            </div>
            <div class="filter-item">
              <p class="label">Category</p>
              <div class="category-values">
                <div class="values">
                  <div  
                    v-for="item in innerFilter.categories?.[0].subCategories.slice(0, !showFilterMoreCategory ? 5 : 9999)"
                    :key="item.id"
                    class="value-item"
                    :data-selected="params.categories.includes(item.id) ? '1' : '0'"
                    @click="handleCategoryClick(item)"
                  >
                    {{ item.name }}
                  </div>    
                </div>

                <div class="show-more-category" v-if="innerFilter.categories?.[0]?.subCategories?.length > 5" @click="showFilterMoreCategory = !showFilterMoreCategory">
                  {{ showFilterMoreCategory ? 'Show Less' : 'Show More' }}
                </div>
              </div>
            </div>

            <div class="filter-item">
              <p class="label">Shipped From</p>
              <div class="values">
                <div  
                  v-for="(item, index) in innerFilter.deliveries"
                  :key="index"
                  class="value-item"
                  :data-selected="params.deliveries.some(c => c.paramName === item.paramName) ? '1' : '0'"
                  @click="handleParamDeliveriesClick(item)"
                >
                  {{ item.name }}
                </div>
              </div>
            </div>

            <div
              v-for="(attr, index) in innerFilter.attrs || []"
              :key="index"
              class="filter-item"
            >
              <p class="label">{{ attr.name }}</p>
              <div class="values">
                <div  
                  v-for="(item, attrIndex) in attr.values"
                  :key="attrIndex"
                  class="value-item"
                  :data-selected="params.attrs.some(([i, v]) => i === index && v === attrIndex) ? '1' : '0'"
                  @click="handleParamAttrsClick(index, attrIndex)"
                >
                  {{ item.value }}
                </div>
              </div>
            </div>
          </div>
          <div class="bottom-options">
            <van-button size="small" style="width: 160px;color: var(--main_color)" plain type="primary" @click="handleDefineClick">Define</van-button>
            <van-button size="small" style="width: 160px;" plain @click="handleResetClick">Reset</van-button>
          </div>
        </div>
      </van-popover>
    </div>
    <div class="search-input-box">
      <van-search
        v-model="params.keywords"
        placeholder="Search..."
        right-icon="search"
        left-icon=""
        style="width: 340px;"
        @click-right-icon="searchFn"
        @clear="handleClear"
        @search="searchFn"
      />
    </div>
  </div>
  <div class="search-bar-wrapper category-search-wrapper" style="margin-top: 10px;">
    <div class="category-list">
      <div  
        v-for="item in innerFilter.categories?.[0]?.subCategories?.slice(0, 15) || []"
        :key="item.id"
        class="category-item"
        :data-selected="params.categories.includes(item.id) ? '1' : '0'"
        @click="handleCategoryClick(item)"
      >
        {{ item.name }}
      </div>
    </div>
    <div class="show-more" @click="showAllCategory = true">
      <van-icon name="arrow-down" color="#999999" />
    </div>

    <div class="all-category-card" v-show="showAllCategory">
      <p class="category-title">
        <span>All Hot Categories</span>
        <span class="show-more" @click="showAllCategory = false">
          <van-icon name="arrow-up" color="#999999" />
        </span>
      </p>
      
      <div class="category-list">
        <div  
          v-for="item in innerFilter.categories?.[0].subCategories"
          :key="item.id"
          class="category-item"
          :data-selected="params.categories.includes(item.id) ? '1' : '0'"
          @click="handleCategoryClick(item)"
        >
          {{ item.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ISort } from '@/pages/search.vue';
import type { ISearchParams } from '@/api/models/system';

interface IProps {
  filters: any,
  sortList: ISort[]
}
interface IEmits {
  (e: 'search', params: ISearchParams): void
}
interface IParamAttr {
  paramName: string,
  paramValue: string | number
  name: string
}
interface IParams {
  keywords: string,
  categories: number[],
  deliveries: IParamAttr[],
  attrs: number[][],
  priceTo: string
  priceFrom: string
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>()

const showSort = ref<boolean>(false)
const showFilter = ref<boolean>(false)
const showAllCategory = ref<boolean>(false)
const showFilterMoreCategory = ref<boolean>(false)
const sortActions = ref(props.sortList.map(item => ({ ...item, act: item.text === 'Popular' })))
const params = ref<IParams>({
  priceTo: '',
  priceFrom: '',
  keywords: '',
  categories: [],
  deliveries: [],
  attrs: []
})

const isFilter = ref<boolean>(false)
const innerFilter = ref<any>({ attrs: [], categories: [], deliveries: [] })
watchEffect(() => {
  if(isFilter.value || !props.filters) {
    return
  }
  
  isFilter.value = true
  innerFilter.value = JSON.parse(JSON.stringify(props.filters))
})

const currentSort = computed(() => {
  return sortActions.value.find(item => item.act)
})
const onSelect = (action: any, index: number) => {
  sortActions.value.forEach(item => {
    item.act = false
  })

  sortActions.value.splice(index, 1, { ...action, act: true })

  searchFn()
}

const handleClear = () => {
  params.value.keywords = ''
}
const searchFn = (type?: 'refresh' | 'search') => {
  const { keywords, categories, deliveries, attrs, priceFrom, priceTo } = params.value
  const { attrs: filterAttrs } = innerFilter.value

  const _attrMap: any = {}

  attrs.forEach(([i, v]) => {
    const _base = filterAttrs[i]

    if(filterAttrs) {
      if(!_attrMap[i]) {
        _attrMap[i] = {
          name: _base.name,
          values: [_base.values[v]]
        }
      } else {
        _attrMap[i].values.push(_base.values[v])
      }
    }
  })

  const attrFilters: { name: string, values: { value: any }[]}[] = []
  for(let k in _attrMap) {
    attrFilters.push(_attrMap[k])
  }

  const _p: ISearchParams = {
    keywords: keywords?.trim() || '',
    attrFilters: JSON.stringify(attrFilters),
    categoryIds: categories,
  }

  if(priceFrom && priceTo) {
    _p.priceFrom = priceFrom
    _p.priceTo = priceTo
  }
  
  deliveries.forEach(({ paramName, paramValue}) => {
    _p[paramName as keyof ISearchParams] = paramValue
  })

  const sortBy = sortActions.value.find(item => item.act)?.value

  sortBy && (_p.sortBy = sortBy)

  emits('search', _p)
}

const handleDefineClick = async () => {
  showFilter.value = false
  await searchFn()
}

const handleResetClick = async() => {
  params.value = {
    priceTo: '',
    priceFrom: '',
    keywords: '',
    categories: [],
    deliveries: [],
    attrs: []
  }

  showFilter.value = false
  await searchFn()
}

const handleCategoryClick = (item: any) => {
  const _idx = params.value.categories.findIndex(i => i === item.id)

  _idx < 0 ? params.value.categories.push(item.id) : params.value.categories.splice(_idx, 1)

  showAllCategory.value = false
  searchFn()
}
const handleApplyPriceFilter = () => {
  searchFn()
  showFilter.value = false
}
const handleParamDeliveriesClick = (item: IParamAttr) => {
  const _idx = params.value.deliveries.findIndex(c => c.paramName === item.paramName)

  _idx < 0 ? params.value.deliveries.push(item) : params.value.deliveries.splice(_idx, 1)
}

const handleParamAttrsClick = (attrIndex: number, attrValIndex: number) => {
  const _idx = params.value.attrs.findIndex(([i ,v]) => i === attrIndex && v === attrValIndex)

  _idx < 0 ? params.value.attrs.push([attrIndex, attrValIndex]) : params.value.attrs.splice(_idx, 1)
}
</script>

<style lang="scss" scoped>
.search-bar-wrapper{
  height: 120px;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1001;
  filter: drop-shadow(2px 2px 8px #00000014);
  .search-input-box{
    padding-right: 80px;
  }
}
.action-text[data-selected="1"]{
  color: var(--main_color);
}
.filter-container::v-deep(.van-cell) {
  padding: 10px 16px !important;
}
.filter-container::v-deep(.van-button--normal) {
  height: 72px !important;
}
.filter-container{
  width: var(--viewport-width);
  padding: 30px;
  color: #666666;
  padding-bottom: 140px;
  position: relative;
  .close-icon{
    position: absolute;
    top: 30px;
    right: 30px;
    z-index: 1;
    cursor: pointer;
    i{
      font-size: 44px;
    }
  }
  .inner-filter-container{
    max-height: 55vh;
    overflow-y: auto;
    @include pc__scrollbar('', 12px);
  }

  .filter-item{
    display: flex;
    line-height: 80px;
    .filter-price-values{
      display: flex;
      align-items: center;
      .filter-price{
        display: flex;
        align-items: center;
      }
      .price-input{
        border: 1px solid #CCCCCC;
      }
    }
    
    .label{
      width: 300px;
      min-width: 300px;
      font-weight: 600;
    }
    .values{
      position: relative;
      flex: 1;
      .value-item{
        float: left;
        margin-right: 40px;
        cursor: pointer;
        user-select: none;

        &:hover{
          color: var(--main_color);
        }
      }
      .value-item[data-selected="1"]{
        color: var(--main_color);
      }
    }
    .category-values{
      display: flex;
      width: 100%;
      .show-more-category{
        border-left: 1px solid #CCCCCC;
        min-width: 200px;
        text-align: center;
        color: var(--main_color);
        cursor: pointer;
      }
    }
  }
  .bottom-options{
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 130px;
    background-color: #fff;
    z-index: 1;
    border-top: 1px solid #CCCCCC;
    display: flex;
    justify-content: center;
    align-items: center;
    button{
      margin: 0 40px;
    }
  }
}
.reference-text{
  color: #666;
  line-height: 100%;
  padding: 0 40px;
  cursor: pointer;
}
.category-search-wrapper{
  z-index: 1002;
}
.category-list {
  flex: 1;
  overflow: hidden;
  position: relative;
  padding-left: 30px;
  display: flex;
  flex-wrap: nowrap;
  .category-item{
    padding: 0 30px;
    margin-right: 20px;
    height: 60px;
    white-space: nowrap;
    line-height: 60px;
    border-radius: 60px;
    font-size: 26px;
    background-color: rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.06);
    cursor: pointer;
  }
  .category-item[data-selected="1"]{
    border-color: var(--main_color);
    color: var(--main_color);
    background: #dd31311a;
  }
}
.all-category-card{
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 20px 20px 20px;
  background-color: #fff;
  filter: drop-shadow(2px 2px 8px #00000014);
  user-select: none;

  .category-title{
    height: 120px;
    line-height: 120px;
    display: flex;
    justify-content: space-between;
    .show-more{
      border: none;
    }
  }
  .category-list{
    flex-wrap: wrap;
    z-index: 99;
    padding-left: 0;
    .category-item{
      margin-bottom: 20px;
    }
  }
}
.show-more{
  width: 120px;
  min-width: 120px;
  cursor: pointer;
  border-left: 1px solid #CCCCCC;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  i{
    font-size: 40px;
  }
  &:hover{
    i {color: var(--default_color) !important;}
  }
}
.mask{
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1002;
  background-color: transparent
}
</style>
